import { DoughnutCard, FoodsIconButton } from "@foodpilot/foods";
import { alpha, Box, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import { useGetOneIngredientReferenceUnified } from "src/api/client-api/ingredientReferenceUnified/useGetOneIngredientReferenceUnified";
import {
    buildCompositeId,
    type IngredientReferenceSource,
    type IngredientReferenceUnified,
} from "src/api/client-api/ingredientReferenceUnified/types";
import type { MainScore } from "src/api/client-api/olapScores/olapScoresSchema";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { ScoreSelectHeader } from "src/pages/Product/v2/ProductFormV2/ScoreLiveSidebar/ScoreSelectHeader";
import { TotalScoreCard } from "src/pages/Product/v2/ProductFormV2/ScoreLiveSidebar/TotalScoreCard";
import type { ScoreWithVariation } from "src/services/ScoreService";

type IngredientReferenceScorePreviewProps = {
    ingredientReferenceId: number | null;
    referenceSource: IngredientReferenceSource;
    isNarrow: boolean;
    stickyTop: number;
};

export const resolveIngredientReferenceMainScore = (
    ingredientReference: IngredientReferenceUnified | undefined,
    selectedScore: ScoreWithVariation,
): MainScore => ({
    score: {
        value: ingredientReference?.scores.find(({ scoreId }) => scoreId === selectedScore.scoreId)?.value ?? null,
    },
    unit: selectedScore.unit,
    label: selectedScore.group,
    type: selectedScore.type,
    displayMode: selectedScore.displayMode,
    defaultDisplay: selectedScore.defaultDisplay ?? true,
    dimensions: [],
    variations: [],
    subscores: {},
});

type IngredientReferenceScoreContentProps = Pick<
    IngredientReferenceScorePreviewProps,
    "ingredientReferenceId" | "referenceSource"
>;

const IngredientReferenceScoreContent = ({
    ingredientReferenceId,
    referenceSource,
}: IngredientReferenceScoreContentProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { lastSelectedVariation, selectedScorePrecision } = useEntityScores();
    const compositeId =
        ingredientReferenceId === null ? undefined : buildCompositeId(referenceSource, ingredientReferenceId);
    const { ingredientReference, isError, isLoading, error, refetch } =
        useGetOneIngredientReferenceUnified(compositeId);
    const mainScore = resolveIngredientReferenceMainScore(ingredientReference, lastSelectedVariation);
    const hasSelectedReference = ingredientReferenceId !== null;
    const referenceTooltip =
        referenceSource === "foodpilot" ?
            t("score_source.ingredient_reference.tooltip", "Score from a default ingredient (platform reference).")
        :   t("score_source.sure.tooltip", "Score from the Sure reference.");
    const referenceTagColors = theme.custom.dataviz.colorsWithExtra[9];
    const transparentReferenceTag = alpha(referenceTagColors.variant[100], 0);

    return (
        <DoughnutCard>
            <Box data-testid="ingredient-reference-score-header" sx={{ width: "100%" }}>
                <ScoreSelectHeader />
            </Box>
            <Stack sx={(theme) => ({ gap: theme.spacing(1.5), padding: theme.spacing(2), width: "100%" })}>
                <Box
                    data-testid="ingredient-reference-score-tag-row"
                    sx={{ display: "flex", justifyContent: "flex-end", minWidth: 0 }}
                >
                    <Box
                        sx={(theme) => ({
                            alignItems: "center",
                            backgroundColor: referenceTagColors.variant[100],
                            borderRadius: theme.spacing(0.5),
                            color: referenceTagColors.main,
                            display: "inline-flex",
                            gap: theme.spacing(0.25),
                            height: theme.spacing(2.75),
                            minWidth: 0,
                            padding: theme.spacing(0.25, 0.5),
                            position: "relative",
                            width: "fit-content",
                        })}
                    >
                        <Typography component="span" variant="small-bold" sx={{ color: referenceTagColors.main }}>
                            {t("ingredient.form.reference_score.tag", "Reference")}
                        </Typography>
                        <FoodsIconButton
                            size="xs"
                            icon="information"
                            buttonProps={{
                                "aria-label": referenceTooltip,
                                type: "button",
                                sx: (theme) => ({
                                    backgroundColor: transparentReferenceTag,
                                    color: referenceTagColors.main,
                                    height: theme.spacing(2),
                                    minWidth: theme.spacing(2),
                                    padding: 0,
                                    position: "static",
                                    width: theme.spacing(2),
                                    "&:hover, &:active": { backgroundColor: transparentReferenceTag },
                                    "&::before": {
                                        content: '""',
                                        inset: 0,
                                        position: "absolute",
                                    },
                                }),
                            }}
                            tooltip={{ text: referenceTooltip, props: { placement: "top" } }}
                        />
                    </Box>
                </Box>
                <TotalScoreCard
                    mainScore={mainScore}
                    unit={lastSelectedVariation.unit}
                    precision={selectedScorePrecision}
                    referenceScore={undefined}
                    referenceName={undefined}
                    improvement={lastSelectedVariation.improvement}
                    weight={{ captionWeightG: null, unit: null }}
                    isLoading={hasSelectedReference && isLoading}
                    scoreQueryError={isError ? error : undefined}
                    retryScoreQuery={isError ? refetch : undefined}
                    emptyGuidance={
                        hasSelectedReference ? null : (
                            t(
                                "ingredient.form.reference_score.empty_guidance",
                                "Choose a reference to get your ingredient's initial score.",
                            )
                        )
                    }
                />
            </Stack>
        </DoughnutCard>
    );
};

/** Exact Ingredient creation reference score card from Figma `12819:70117`. */
export const IngredientReferenceScorePreview = ({
    ingredientReferenceId,
    referenceSource,
    isNarrow,
    stickyTop,
}: IngredientReferenceScorePreviewProps) => {
    const { t } = useTranslation();

    return (
        <Box
            data-testid="ingredient-reference-score-aside"
            aria-label={t("ingredient.form.reference_score.aria", "Reference score")}
            sx={(theme) => ({
                maxWidth: "100%",
                minWidth: 0,
                position: isNarrow ? "static" : "sticky",
                top: isNarrow ? undefined : stickyTop,
                width: isNarrow ? "100%" : theme.custom.doughnutCard.width,
                "& > .MuiStack-root": {
                    minWidth: 0,
                    width: "100%",
                },
            })}
        >
            <IngredientReferenceScoreContent
                ingredientReferenceId={ingredientReferenceId}
                referenceSource={referenceSource}
            />
        </Box>
    );
};
